import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus } from "@theme/focus";
import { onAnimationEnd } from "@theme/utilities";

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ["details"];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("keyup", this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupAnchorLinkHandlers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("keyup", this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== "Escape") return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute("open");
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest("details") ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "true");

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add("menu-open");
      setTimeout(() => {
        trapFocus(details);
      }, 0);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector("summary");

    if (!summary) return;

    summary.setAttribute("aria-expanded", "false");
    details.classList.remove("menu-open");

    onAnimationEnd(details, () => {
      reset(details);
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll(
          "details[open]:not(accordion-custom > details)"
        );
        openDetails.forEach(reset);
      } else {
        setTimeout(() => {
          trapFocus(this.refs.details);
        }, 0);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty("will-change", "unset");
        target.removeEventListener(
          "animationend",
          removeWillChangeOnAnimationEnd
        );
      }
    }
    const allAnimated = this.querySelectorAll(".menu-drawer__animated-element");
    allAnimated.forEach((element) => {
      element.addEventListener("animationend", removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll(
      "accordion-custom .details-content"
    );

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add("details-content--no-animation");
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove("details-content--no-animation");
        }
      });
    }, 100);
  }

  /**
   * Setup click handlers for in-drawer anchor links.
   *
   * The page uses GSAP ScrollSmoother (theme.liquid), which drives the viewport
   * via transform instead of native scroll.  We use the exposed
   * window.scrollSmoother.scrollTo(element, smooth, position) to scroll.
   */
  #setupAnchorLinkHandlers() {
    const anchorLinks = this.querySelectorAll('a[href^="#"], a[href^="/#"]');

    anchorLinks.forEach((anchor) => {
      anchor.addEventListener("click", (e) => {
        const href = anchor.getAttribute("href") ?? "";
        // Normalise both `#id` and `/#id` to just the id string
        const targetId = href.startsWith("/#") ? href.slice(2) : href.slice(1);
        if (!targetId) return;

        let targetElement = document.getElementById(targetId);
        // In prod, section IDs can differ (e.g. different template number, _ vs __).
        // Fallback 1: type prefix for single-per-page sections (ProductSpecs-, Hero-, etc.).
        const prefixFallbackTypes = ["ProductSpecs-", "Hero-", "ProductInformation-"];
        const prefix = prefixFallbackTypes.find((p) => targetId.startsWith(p));
        if (!targetElement && prefix) {
          const byPrefix = document.querySelector(`[id^="${CSS.escape(prefix)}"]`);
          if (byPrefix instanceof HTMLElement) targetElement = byPrefix;
        }
        // Fallback 2: shopify-section-template--* — match by stable suffix (e.g. section_B86EPk, product_specs_ey9zJy).
        // Template number and _ vs __ differ in prod; the suffix (section type + key) is stable.
        if (!targetElement && targetId.startsWith("shopify-section-template--")) {
          const afterPrefix = targetId.replace(/^shopify-section-template--/, "");
          const suffix = afterPrefix.replace(/^\d+_*/, "");
          if (suffix) {
            const bySuffix = document.querySelector(
              `[id$="${CSS.escape(suffix)}"]`
            );
            if (bySuffix instanceof HTMLElement) targetElement = bySuffix;
          }
        }
        if (!targetElement) {
          e.preventDefault();
          return;
        }

        e.preventDefault();

        // Release scroll-lock so ScrollSmoother can operate.
        document.documentElement.removeAttribute("scroll-lock");

        // Immediately tear down the drawer (no close animation).
        removeTrapFocus();
        reset(this.refs.details);
        this.querySelectorAll(
          "details[open]:not(accordion-custom > details)"
        ).forEach(reset);

        // Update the URL hash (always use bare #id form regardless of link format).
        history.pushState(null, "", `#${targetId}`);

        // Defer scroll so it runs after layout has settled from closing the drawer.
        // Otherwise ScrollTrigger/ScrollSmoother refresh (triggered by the DOM change) can
        // run before or during the scroll tween and reset position to 0.
        const smoother = /** @type {any} */ (window).scrollSmoother;
        const runScroll = () => {
          if (smoother && typeof smoother.scrollTo === "function") {
            smoother.scrollTo(targetElement, true, "top top");
          } else {
            targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        };
        requestAnimationFrame(() => {
          requestAnimationFrame(runScroll);
        });
      });
    });
  }

}

if (!customElements.get("header-drawer")) {
  customElements.define("header-drawer", HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove("menu-open");
  element.removeAttribute("open");
  element.querySelector("summary")?.setAttribute("aria-expanded", "false");
}
